import { FC, useEffect, useState } from 'react';
import { AddressType, AvaSuburbs, Customer, LoqateAddress, LoqateFullAddress } from '@models';
import { Grid, Typography, Button } from '@mui/material';
import { Box } from '@mui/system';
import { LoqateAddressFinder } from '../LoqateAddressFinder/LoqateAddressFinder';
import { AddressFormData, AU_STATES, IState, LoqateInitialState, postcodeError } from '@constants';
import { useRetreiveAddressQuery } from '@services';

interface Props {
  availableSuburbs: AvaSuburbs[];
  customer: Customer;
  loqateFullAddress: LoqateFullAddress;
  onSubmit: (data: AddressFormData) => void;
  resetAutoComplete: boolean;
  setLoqateFullAddress: (address: LoqateFullAddress) => void;
}

export const AddNewAddressAutocomplete: FC<Props> = ({
  availableSuburbs,
  customer,
  loqateFullAddress,
  onSubmit,
  resetAutoComplete,
  setLoqateFullAddress,
}) => {
  const [isValidPostcode, setIsValidPostcode] = useState(true);
  const { data: addressData } = useRetreiveAddressQuery(
    { addressId: loqateFullAddress.id },
    {
      skip: !loqateFullAddress?.id,
    },
  );

  const loqateAddress: LoqateAddress = addressData?.length ? addressData[0] : LoqateInitialState;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const loqateStateProvince: IState = addressData?.length ? AU_STATES.find((item) => item.id === addressData[0].state)! : AU_STATES[0];

  useEffect(() => {
    setIsValidPostcode(!!availableSuburbs?.find((area) => area.suburb_postcode === loqateAddress.postcode));
  }, [availableSuburbs, loqateAddress]);

  const saveLoqateAddress = () => {
    onSubmit({
      id: 0,
      address1: `${loqateAddress.streetNumber ? loqateAddress.streetNumber : ''} ${loqateAddress.street ? loqateAddress.street : ''}`,
      address2: `${loqateAddress.subdwelling ? loqateAddress.subdwelling : ''} `,
      address_type: 'residential' as AddressType,
      city: loqateAddress.locality,
      company: '',
      country: 'Australia',
      country_code: 'AU',
      customer_id: customer.id,
      first_name: customer.first_name,
      last_name: customer.last_name,
      phone: customer.phone,
      state_or_province: loqateStateProvince.id,
      postal_code: loqateAddress.postcode,
      longitude: loqateAddress.attributes?.Longitude,
      latitude: loqateAddress.attributes?.Latitude,
      gnafid: loqateAddress.attributes?.GNAFPID,
    });
  };

  return (
    <>
      <LoqateAddressFinder
        onSelectedAddress={(selectedItem) => {
          if (!selectedItem) return;

          setLoqateFullAddress(selectedItem as LoqateFullAddress);
        }}
        isReset={resetAutoComplete}
      />
      {loqateFullAddress?.fullAddress !== '' && (
        <Box py={2}>
          <Grid container spacing={2} justifyContent="space-around">
            <Grid item xs={10}>
              <Typography variant="body1">{loqateFullAddress.fullAddress}</Typography>
              {!isValidPostcode && (
                <Typography variant="caption" color="red">
                  {postcodeError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={saveLoqateAddress}
                data-testid="auto-address-submit-button"
                disabled={!isValidPostcode}
                fullWidth
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
